import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { AdminContentManager } from '@/components/admin/AdminContentManager';
import { AdminSettings } from '@/components/admin/AdminSettings';
import { 
  Shield,
  Gauge,
  Watch,
  Compass,
  Settings,
  FileText,
  Loader2,
  AlertCircle,
  LayoutDashboard,
  Users,
  Bell
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const SUPER_ADMIN_EMAIL = 'solutions.oko@gmail.com';
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds

export default function Admin() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const checkAccess = async () => {
    try {
      setError(null);
      setLoading(true);

      if (!currentUser?.email) {
        navigate('/login');
        return;
      }

      const isSuperAdmin = currentUser.email === SUPER_ADMIN_EMAIL;
      setIsAdmin(isSuperAdmin);

      if (!isSuperAdmin) {
        console.log("Accès refusé - Email actuel:", currentUser.email);
        toast({
          variant: "destructive",
          title: "Accès refusé",
          description: "Vous n'avez pas les droits d'accès à cette section.",
        });
        navigate('/dashboard');
        return;
      }

      console.log("Accès autorisé - Super Admin confirmé");
      toast({
        title: "Accès autorisé",
        description: "Bienvenue, Super Administrateur",
      });

    } catch (error) {
      console.error('Error checking access:', error);
      
      if (retryCount < MAX_RETRIES) {
        setRetryCount(prev => prev + 1);
        setError('Erreur de connexion. Nouvelle tentative dans quelques secondes...');
        
        setTimeout(() => {
          checkAccess();
        }, RETRY_DELAY);
      } else {
        setError('Impossible de vérifier vos droits d\'accès. Veuillez réessayer plus tard.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAccess();
  }, [currentUser, navigate, toast]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          <p className="text-gray-600">Vérification des droits d'accès...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6">
            <div className="flex flex-col items-center gap-4">
              <AlertCircle className="w-12 h-12 text-red-500" />
              <h2 className="text-xl font-semibold text-red-600">Erreur de connexion</h2>
              <p className="text-center text-gray-600">{error}</p>
              <Button
                onClick={() => {
                  setRetryCount(0);
                  setError(null);
                  checkAccess();
                }}
                className="w-full bg-[#54075B] hover:bg-[#54075B]/90"
              >
                Réessayer
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="w-full"
              >
                Retour au tableau de bord
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
      <div className="max-w-7xl mx-auto space-y-6">
        <Card className="backdrop-blur-sm bg-white/90">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#54075B]" />
              <h1 className="text-2xl font-bold">Administration Centrale</h1>
            </div>
            <p className="text-gray-600">
              Gérez l'ensemble de la plateforme Human & Entity Design
            </p>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="dashboard" className="space-y-6">
              <TabsList className="grid grid-cols-4 gap-4">
                <TabsTrigger value="dashboard" className="flex items-center gap-2">
                  <LayoutDashboard className="w-4 h-4" />
                  Tableau de bord
                </TabsTrigger>
                <TabsTrigger value="users" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Utilisateurs
                </TabsTrigger>
                <TabsTrigger value="content" className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Contenus
                </TabsTrigger>
                <TabsTrigger value="settings" className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Paramètres
                </TabsTrigger>
              </TabsList>

              <TabsContent value="dashboard">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 bg-purple-100 rounded-lg">
                          <Gauge className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Baromètre HED</h3>
                          <p className="text-sm text-gray-500">Administration du baromètre</p>
                        </div>
                      </div>
                      <Button 
                        onClick={() => navigate('/barometre/admin')}
                        className="w-full"
                      >
                        Accéder
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 bg-blue-100 rounded-lg">
                          <Watch className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Projector Watch</h3>
                          <p className="text-sm text-gray-500">Administration de Projector Watch</p>
                        </div>
                      </div>
                      <Button 
                        onClick={() => navigate('/projector-watch/admin')}
                        className="w-full"
                      >
                        Accéder
                      </Button>
                    </CardContent>
                  </Card>

                  <Card className="hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 bg-green-100 rounded-lg">
                          <Compass className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Align</h3>
                          <p className="text-sm text-gray-500">Administration d'Align</p>
                        </div>
                      </div>
                      <Button 
                        onClick={() => navigate('/align/admin')}
                        className="w-full"
                      >
                        Accéder
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="users">
                {/* Contenu de la gestion des utilisateurs */}
              </TabsContent>

              <TabsContent value="content">
                <AdminContentManager />
              </TabsContent>

              <TabsContent value="settings">
                <AdminSettings />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}